import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT, SERVICE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", phone: "", email: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    const text =
      `New enquiry from the MJL website:\n\n` +
      `Name: ${form.name}\n` +
      `Phone: ${form.phone}\n` +
      `Email: ${form.email}\n` +
      `Service: ${form.service}\n` +
      `Message: ${form.message}`;
    const waUrl = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(text)}`;

    // keep a backup copy in the database (best-effort, non-blocking)
    axios.post(`${API}/enquiries`, form).catch(() => {});

    // open WhatsApp with the enquiry pre-filled (opened within the click gesture)
    window.open(waUrl, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — just press send to reach us!");
    setForm(empty);
  };

  const inputCls =
    "bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus-visible:ring-[#E63946] focus-visible:border-[#E63946]";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-28 surface-a overflow-hidden">
      <div className="grain absolute inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E63946]">Contact</p>
          <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-steel">
            Need a Quote? Get in Touch Today
          </h2>
          <p className="mt-4 text-gray-400">
            Fill in the form for a free, no-obligation quote — or call us directly for
            emergencies and fast response.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl card-grey elev border border-white/10 p-6 sm:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-200">Name</label>
                <Input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-200">Phone Number</label>
                <Input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="07XXX XXXXXX"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-200">Email</label>
              <Input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-200">Service Required</label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="contact-service-select" className="bg-white/5 border-white/20 text-white focus:ring-[#E63946]">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2f] border-white/15 text-white">
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-[#E63946] focus:text-white">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-200">Message</label>
              <Textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us about the job..."
                rows={4}
                className={inputCls}
              />
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              className="btn-press w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white px-7 py-4 text-base font-bold"
            >
              <MessageCircle className="w-5 h-5" /> Send Enquiry via WhatsApp
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="contact-phone-link"
                className="rounded-2xl card-grey elev border border-white/10 p-6 hover:border-[#E63946]/50 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#E63946] text-white">
                  <Phone className="w-5 h-5" />
                </span>
                <p className="mt-4 text-sm text-gray-400">Call us</p>
                <p className="font-semibold text-white">{CONTACT.phone}</p>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="rounded-2xl card-grey elev border border-white/10 p-6 hover:border-[#E63946]/50 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#E63946] text-white">
                  <Mail className="w-5 h-5" />
                </span>
                <p className="mt-4 text-sm text-gray-400">Email us</p>
                <p className="font-semibold break-all text-sm text-white">{CONTACT.email}</p>
              </a>
            </div>

            <div className="rounded-2xl card-grey elev border border-white/10 p-6">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <MapPin className="w-5 h-5 text-[#E63946]" /> {CONTACT.area}
              </div>
              <div className="mt-4 rounded-xl overflow-hidden border border-white/10">
                <iframe
                  title="MJL Maintenance & Locks service area map"
                  data-testid="contact-map"
                  src="https://www.google.com/maps?q=Buckinghamshire,UK&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0, filter: "grayscale(1) contrast(1.05) opacity(0.9)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
